import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import SearchSection from './SearchSection';
import StudentTable from './StudentTable';
import PaymentDetail from './PaymentDetail';
import { 
  getCampusDropdown, 
  getEstudiantesConAdeudos,
  getDetalleEstudiantePagos,
  crearPagoYAplicar
} from '../../api/aplicacionPagos';
import styles from '../../styles/AplicacionPagosStyles/AplicacionPagos.module.css';

function AplicacionPagosScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [campusName, setCampusName] = useState(() => localStorage.getItem('selectedCampus') || '');
  const [campusId, setCampusId] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromCargosManuales, setFromCargosManuales] = useState(false);

  // Sincronizar campus con HeaderGlobal (localStorage / evento personalizado) y cargar estudiantes
  useEffect(() => {
    const handleStorageChange = () => {
      const newCampus = localStorage.getItem('selectedCampus') || '';
      setCampusName(newCampus);
      setCampusId(null);
      if (!fromCargosManuales) {
        loadStudents(newCampus, searchTerm);
      }
    };

    const handleCampusChanged = (e) => {
      const newCampus = (e.detail && e.detail.campus) || localStorage.getItem('selectedCampus') || '';
      setCampusName(newCampus);
      setCampusId(null);
      if (!fromCargosManuales) {
        loadStudents(newCampus, searchTerm);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('campusChanged', handleCampusChanged);

    // Carga inicial
    const initialCampus = localStorage.getItem('selectedCampus') || campusName;
    setCampusName(initialCampus);
    if (initialCampus && !fromCargosManuales) {
      loadStudents(initialCampus, searchTerm);
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('campusChanged', handleCampusChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Verificar si viene de Cargos Manuales con datos del estudiante
  useEffect(() => {
    if (location.state?.fromCargosManuales && location.state?.studentId) {
      setFromCargosManuales(true);
      
      // Si viene campus, usarlo
      if (location.state.campusId) {
        setCampusId(location.state.campusId);
      }
      
      // Cargar el detalle del estudiante automáticamente
      loadStudentFromCargosManuales(location.state.studentId);
    }
  }, [location.state]);

  // Cargar estudiante que viene de Cargos Manuales
  const loadStudentFromCargosManuales = async (studentId) => {
    try {
      setLoading(true);
      setError(null);
      
      const detalleCompleto = await getDetalleEstudiantePagos(studentId);
      
      // Mapear campos del backend al formato esperado por PaymentDetail
      const estudianteMapeado = {
        ...detalleCompleto,
        periodoActual: detalleCompleto.periodo_actual || detalleCompleto.ciclo_escolar || 'N/A'
      };
      
      setSelectedStudent(estudianteMapeado);
    } catch (err) {
      console.error('Error al cargar detalle del estudiante:', err);
      setError('Error al cargar información del estudiante. Intente buscar manualmente.');
      setFromCargosManuales(false);
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async (campusNameParam, search = '') => {
    const effectiveCampusName = campusNameParam || localStorage.getItem('selectedCampus') || '';
    if (!effectiveCampusName) {
      setStudents([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      let resolvedCampusId = null;

      // Soportar "Todos los Campus" / "Todos"
      if (
        effectiveCampusName === 'Todos' ||
        effectiveCampusName === 'Todos los Campus' ||
        effectiveCampusName === 'all'
      ) {
        resolvedCampusId = 'all';
      } else if (campusId && effectiveCampusName === campusName) {
        resolvedCampusId = campusId;
      } else {
        let campusList = [];
        const cached = localStorage.getItem('aplicacionPagosCampusDropdownCache');
        if (cached) {
          try {
            campusList = JSON.parse(cached) || [];
          } catch (e) {
            campusList = [];
          }
        }

        if (!Array.isArray(campusList) || campusList.length === 0) {
          campusList = await getCampusDropdown();
          if (Array.isArray(campusList)) {
            localStorage.setItem('aplicacionPagosCampusDropdownCache', JSON.stringify(campusList));
          }
        }

        if (Array.isArray(campusList)) {
          const campusObj = campusList.find((c) =>
            c.nombre === effectiveCampusName ||
            c.nombre_campus === effectiveCampusName ||
            String(c.id) === String(effectiveCampusName) ||
            String(c.id_campus) === String(effectiveCampusName)
          );
          if (campusObj) {
            resolvedCampusId = campusObj.id || campusObj.id_campus || null;
          }
        }
      }

      if (!resolvedCampusId) {
        setStudents([]);
        setCampusId(null);
        return;
      }

      setCampusId(resolvedCampusId);
      const data = await getEstudiantesConAdeudos(resolvedCampusId, search || searchTerm);
      setStudents(data);
    } catch (err) {
      console.error('Error al cargar estudiantes:', err);
      setError('Error al cargar estudiantes');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadStudents(campusName, searchTerm);
  };

  const handleSelectStudent = async (student) => {
    try {
      setLoading(true);
      const detalleCompleto = await getDetalleEstudiantePagos(student.id);
      
      // Mapear campos del backend al formato esperado por PaymentDetail
      const estudianteMapeado = {
        ...detalleCompleto,
        periodoActual: detalleCompleto.periodo_actual || detalleCompleto.ciclo_escolar || 'N/A'
      };
      
      setSelectedStudent(estudianteMapeado);
    } catch (err) {
      console.error('Error al cargar detalle del estudiante:', err);
      setError('Error al cargar información del estudiante');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSearch = () => {
    setSelectedStudent(null);
    setFromCargosManuales(false);
    loadStudents(campusName, searchTerm); // Recargar lista por si hubo cambios
  };

  // Handler para regresar a Cargos Manuales
  const handleBackToCargosManuales = () => {
    navigate('/generacion-cargos-manuales');
  };

  const handleRegisterPayment = async (paymentData) => {
    try {
      setLoading(true);
      const response = await crearPagoYAplicar(paymentData);
      return response;
    } catch (err) {
      console.error('Error al registrar pago:', err);
      const mensaje = err.message || 'Error al registrar el pago';
      throw new Error(mensaje);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.layout}>
      <div className={styles.sidebarColumn}>
        <Sidebar />
      </div>
      
      <div className={styles.contentColumn}>
        <div className={styles.contentArea}>
          <HeaderGlobal title="Aplicación de Pagos" />
          
          {error && (
            <div style={{ padding: '20px', color: 'red', textAlign: 'center' }}>
              {error}
            </div>
          )}
          
          {loading && (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              Cargando...
            </div>
          )}
          
          {!selectedStudent ? (
            <>
              <SearchSection 
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onSearch={handleSearch}
                loading={loading}
              />
              
              <StudentTable 
                students={students}
                onSelectStudent={handleSelectStudent}
                loading={loading}
              />
            </>
          ) : (
            <PaymentDetail 
              student={selectedStudent}
              onBack={handleBackToSearch}
              onRegisterPayment={handleRegisterPayment}
              onBackToCargosManuales={fromCargosManuales ? handleBackToCargosManuales : null}
              campus={campusId}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default AplicacionPagosScreen;
